import {
  Alert,
  ControlLabel,
  Dialog,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
} from "@all-manager/design-system-v2";
import { Controller } from "react-hook-form";
import ContactNameAutocomplete from "../../../../../components/ContactNameAutocomplete/ContactNameAutocomplete";
import DateField from "../../../../../components/modals/components/DateFieldOk";
import DescriptionFieldOk from "../../../../../components/modals/components/DescriptionFieldOk";
import ProviderAutocomplete from "../../../../../components/ProviderAutocomplete/ProviderAutocomplete";
import type { IncidentDatabase } from "../../../../../types";
import DocumentsFormSection from "../../../../documents/external-components/DocumentsFormSection/DocumentsFormSection";
import RevealFieldButton from "../../actionModales/RevealFieldButton/RevealFieldButton";
import { useEditIncidentModal } from "./useEditIncidentModal";

type Props = {
  incident: IncidentDatabase;
  subjectId: string;
  onClose: () => void;
};

export default function EditIncidentModal(props: Props) {
  const {
    form,
    handleConfirm,
    isLoading,
    serverError,
    isDocumentLoading,
    showDocuments,
    revealDocuments,
  } = useEditIncidentModal(props);

  return (
    <Dialog
      open
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      title="Modifier l'incident"
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: 'Enregistrer',
          onClick: form.handleSubmit(handleConfirm),
          loading: isLoading,
          disabled: isDocumentLoading,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: props.onClose,
          disabled: isLoading,
        },
      }}
    >
      <Stack spacing={2}>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}

        <Controller
          name="title"
          control={form.control}
          render={({ field, fieldState }) => (
            <TextField
              name={field.name}
              inputRef={field.ref}
              onBlur={field.onBlur}
              label="Titre de l'incident"
              value={field.value}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              fullWidth
              required
              autoFocus
            />
          )}
        />

        <DateField
          name="date"
          form={form}
          label="Date de constatation"
          isTimeManaged={false}
          required
        />

        <Controller
          name="status"
          control={form.control}
          render={({ field }) => (
            <RadioGroup
              row
              label="Statut"
              name={field.name}
              value={field.value}
              onChange={(event) => field.onChange(event.target.value)}
            >
              <ControlLabel value="in_progress" control={<Radio />} label="En cours" />
              <ControlLabel value="resolved" control={<Radio />} label="Terminé" />
            </RadioGroup>
          )}
        />

        <Controller
          control={form.control}
          name="provider_name"
          render={({ field, fieldState }) => (
            <ProviderAutocomplete
              label="Prestataire"
              value={field.value ?? ''}
              onChange={field.onChange}
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={form.control}
          name="contact_name"
          render={({ field, fieldState }) => (
            <ContactNameAutocomplete
              label="Contact"
              value={field.value ?? ''}
              onChange={field.onChange}
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <DescriptionFieldOk
          form={form}
          name="comment"
          label="Commentaire / Description"
        />

        {showDocuments ? (
          isDocumentLoading ? (
            <Skeleton variant="rounded" height={120} aria-label="Chargement des documents" />
          ) : (
            <Controller
              name="documents"
              control={form.control}
              render={({ field }) => (
                <DocumentsFormSection
                  documents={field.value}
                  onChange={field.onChange}
                  subjectId={props.subjectId}
                />
              )}
            />
          )
        ) : (
          <RevealFieldButton label="Ajouter des photos ou documents" onClick={revealDocuments} />
        )}
      </Stack>
    </Dialog>
  );
}
